import { useRef, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BsFileImage } from 'react-icons/bs';
import { FiUploadCloud, FiDownload, FiCopy, FiCheck } from 'react-icons/fi';
import SEO from '../components/SEO';
import { base64ConverterSchemas } from '../schemas';
import './Base64Converter.css';

const MAX_FILE_SIZE = 20 * 1024 * 1024;

const Base64Converter = () => {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  
  const fileInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState(tabParam === 'toImage' ? 'toImage' : 'toBase64');
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [base64String, setBase64String] = useState('');
  const [base64Input, setBase64Input] = useState('');
  const [convertedImage, setConvertedImage] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [converting, setConverting] = useState(false);
  const [fileName, setFileName] = useState('');
  const [showFullBase64, setShowFullBase64] = useState(false);

  // Update active tab when URL parameter changes
  useEffect(() => {
    if (tabParam === 'toImage') {
      setActiveTab('toImage');
    } else if (tabParam === 'toBase64') {
      setActiveTab('toBase64');
    }
  }, [tabParam]);

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      handleFileUpload(file);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      handleFileUpload(file);
    }
  };

  const handleFileUpload = (file) => {
    if (file.size > MAX_FILE_SIZE) {
      setError('File size exceeds 20MB limit.');
      return;
    }

    setError(null);
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target.result;
      setUploadedImage(result);
      setBase64String(result);
      setShowFullBase64(false);
    };
    reader.readAsDataURL(file);
  };

  const handleCopyBase64 = () => {
    navigator.clipboard.writeText(base64String);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleConvertToImage = () => {
    const trimmedInput = base64Input.trim();
    
    if (!trimmedInput) {
      setError('Please enter a Base64 string.');
      return;
    }

    setError(null);
    setConverting(true);

    setTimeout(() => {
      try {
        // Check if it's a data URL or raw base64
        let imageUrl = trimmedInput;
        
        if (!trimmedInput.startsWith('data:')) {
          // If it's raw base64, add the data URL prefix
          // Try to detect the image format, default to PNG
          imageUrl = `data:image/png;base64,${trimmedInput}`;
        }

        // Validate by trying to create an image
        const img = new Image();
        img.onload = () => {
          setConvertedImage(imageUrl);
          setConverting(false);
        };
        img.onerror = () => {
          setError('Invalid Base64 image string. Please check your input.');
          setConverting(false);
        };
        img.src = imageUrl;
      } catch (err) {
        setError('Failed to convert Base64 to image.');
        setConverting(false);
      }
    }, 3000);
  };

  const handleDownloadImage = () => {
    if (!convertedImage) return;

    const link = document.createElement('a');
    link.href = convertedImage;
    link.download = 'converted-image.png';
    link.click();
  };

  const resetImageToBase64 = () => {
    setUploadedImage(null);
    setBase64String('');
    setFileName('');
    setError(null);
    setShowFullBase64(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const resetBase64ToImage = () => {
    setBase64Input('');
    setConvertedImage(null);
    setError(null);
    setConverting(false);
  };

  const getTruncatedBase64 = () => {
    if (base64String.length <= 500) return base64String;
    return showFullBase64 ? base64String : base64String.substring(0, 500) + '...';
  };

  return (
    <div className="base64-page">
      <SEO 
        title="Base64 Image Converter – Encode & Decode Online"
        description="Convert images to Base64 or decode Base64 to image instantly. Free online Base64 encoder/decoder for developers — no upload limits."
        keywords="base64 converter, image to base64, base64 to image, base64 encoder"
        path="/base64-converter"
        schema={base64ConverterSchemas}
      />
      <div className="base64-container">
        <header className="base64-header">
          <h1 className="base64-title">Base64 Converter</h1>
          <p className="base64-subtitle">Convert images to Base64 and vice versa</p>
        </header>

        {/* Tabs */}
        <div className="base64-tabs">
          <button
            className={`base64-tab ${activeTab === 'toBase64' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('toBase64');
              resetBase64ToImage();
            }}
          >
            Image to Base64
          </button>
          <button
            className={`base64-tab ${activeTab === 'toImage' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('toImage');
              resetImageToBase64();
            }}
          >
            Base64 to Image
          </button>
        </div>

        {error && (
          <div className="error-message">
            <div className="error-content">
              <span className="error-icon">!</span>
              <p className="error-text">{error}</p>
              <button className="error-close" onClick={() => setError(null)}>×</button>
            </div>
          </div>
        )}

        {/* Image to Base64 Tab */}
        {activeTab === 'toBase64' && (
          <div className="tab-content">
            {!uploadedImage ? (
              <motion.div
                className={`upload-area ${isDragging ? 'dragging' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="upload-content">
                  <div className="upload-icon-wrapper">
                    <FiUploadCloud className="upload-icon" />
                  </div>
                  <h2 className="upload-title">Upload an image</h2>
                  <p className="upload-description">
                    Drag and drop an image here, or click to select
                  </p>
                  <button
                    type="button"
                    className="upload-button"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <BsFileImage /> Choose image
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    hidden
                  />
                </div>
                <div className="upload-glow" />
              </motion.div>
            ) : (
              <motion.div
                className="result-container"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="preview-card-small">
                  <img src={uploadedImage} alt="Preview" className="preview-img-small" />
                  <p className="preview-filename">{fileName}</p>
                </div>

                <div className="base64-output-card">
                  <div className="base64-header-section">
                    <h3 className="base64-output-title">Base64 String</h3>
                    <button
                      className={`copy-button ${copied ? 'copied' : ''}`}
                      onClick={handleCopyBase64}
                    >
                      {copied ? <><FiCheck /> Copied</> : <><FiCopy /> Copy</>}
                    </button>
                  </div>
                  <textarea
                    className="base64-textarea"
                    value={getTruncatedBase64()}
                    readOnly
                  />
                  {base64String.length > 500 && (
                    <button
                      className="expand-button"
                      onClick={() => setShowFullBase64(!showFullBase64)}
                    >
                      {showFullBase64 ? 'Show Less' : 'Show More'}
                    </button>
                  )}
                </div>

                <button className="reset-button" onClick={resetImageToBase64}>
                  Upload another image
                </button>
              </motion.div>
            )}
          </div>
        )}

        {/* Base64 to Image Tab */}
        {activeTab === 'toImage' && (
          <div className="tab-content">
            {!convertedImage ? (
              <motion.div
                className="input-container"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="base64-input-card">
                  <h3 className="input-title">Enter Base64 String</h3>
                  <textarea
                    className="base64-input-textarea"
                    placeholder="Paste your Base64 image string here..."
                    value={base64Input}
                    onChange={(e) => setBase64Input(e.target.value)}
                  />
                </div>

                <button
                  className="convert-button"
                  onClick={handleConvertToImage}
                  disabled={!base64Input.trim() || converting}
                >
                  {converting ? 'Converting...' : 'Convert to Image'}
                </button>
              </motion.div>
            ) : (
              <motion.div
                className="result-container"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {converting ? (
                  <div className="converting-state">
                    <motion.div
                      className="spinner"
                      animate={{ rotate: 360 }}
                      transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
                    />
                    <p className="converting-text">Converting...</p>
                  </div>
                ) : (
                  <>
                    <div className="preview-card-large">
                      <img src={convertedImage} alt="Converted" className="preview-img-large" />
                    </div>

                    <div className="action-buttons">
                      <button className="download-button" onClick={handleDownloadImage}>
                        <FiDownload /> Download Image
                      </button>
                      <button className="reset-button" onClick={resetBase64ToImage}>
                        Convert another
                      </button>
                    </div>
                  </>
                )}
              </motion.div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Base64Converter;
